import {
  createContext,
  useContext,
  useState,
  useEffect,
  type ReactNode,
} from 'react'
import { org as orgApi } from '../api/client'

/**
 * Org branding configuration
 */
export interface OrgBranding {
  logoUrl: string | null
  faviconUrl: string | null
  primaryColor: string | null
  secondaryColor: string | null
  customCss: string | null
  showPoweredBy: boolean
}

/**
 * Org registration settings
 */
export interface OrgRegistration {
  mode: 'open' | 'invite_only' | 'domain_restricted'
  allowedDomains: string[]
}

/**
 * Org OAuth configuration
 */
export interface OrgOAuth {
  managed: boolean
  providers: ('google' | 'github' | 'discord' | 'twitter' | 'telegram')[]
}

/**
 * Full org context for multi-tenant operation
 */
export interface OrgContextValue {
  slug: string | null
  name: string | null
  branding: OrgBranding
  registration: OrgRegistration
  oauth: OrgOAuth
  loading: boolean
  error: string | null
}

const defaultBranding: OrgBranding = {
  logoUrl: null,
  faviconUrl: null,
  primaryColor: null,
  secondaryColor: null,
  customCss: null,
  showPoweredBy: true,
}

const defaultRegistration: OrgRegistration = {
  mode: 'open',
  allowedDomains: [],
}

const defaultOAuth: OrgOAuth = {
  managed: false,
  providers: ['google', 'github'],
}

const defaultContext: OrgContextValue = {
  slug: null,
  name: null,
  branding: defaultBranding,
  registration: defaultRegistration,
  oauth: defaultOAuth,
  loading: true,
  error: null,
}

const OrgContext = createContext<OrgContextValue>(defaultContext)

/**
 * Extract org slug from domain or query params
 * Production: core.{slug}.u.software
 * Development: ?org={slug} or default to 'demo'
 */
function extractSlugFromDomain(): string | null {
  const hostname = window.location.hostname

  // Production pattern: core.{slug}.u.software
  const prodMatch = hostname.match(/^core\.(.+)\.u\.software$/)
  if (prodMatch) {
    return prodMatch[1]
  }

  // Also support {slug}.core.u.software pattern
  const altMatch = hostname.match(/^(.+)\.core\.u\.software$/)
  if (altMatch) {
    return altMatch[1]
  }

  // Development: use query param
  const params = new URLSearchParams(window.location.search)
  const orgParam = params.get('org')
  if (orgParam) {
    return orgParam
  }

  // No org context - this is the main u.software identity provider
  return null
}

export interface OrgContextProviderProps {
  children: ReactNode
}

export function OrgContextProvider({ children }: OrgContextProviderProps) {
  const [state, setState] = useState<OrgContextValue>(defaultContext)

  useEffect(() => {
    // Skip org config for utility pages (silent-callback, etc.)
    const isUtilityPage = window.location.pathname === '/silent-callback'
    if (isUtilityPage) {
      setState({
        ...defaultContext,
        loading: false,
      })
      return
    }

    const slug = extractSlugFromDomain()

    // If no slug, use defaults (main u.software IdP)
    if (!slug) {
      setState({
        ...defaultContext,
        loading: false,
      })
      return
    }

    // Fetch org configuration
    const fetchOrg = async () => {
      try {
        // Org is determined from subdomain by the backend
        const orgConfig = await orgApi.getPublicConfig()

        setState({
          slug,
          name: orgConfig.name,
          branding: {
            logoUrl: orgConfig.branding?.logo_url ?? null,
            faviconUrl: orgConfig.branding?.favicon_url ?? null,
            primaryColor: orgConfig.branding?.primary_color ?? null,
            secondaryColor: orgConfig.branding?.secondary_color ?? null,
            customCss: orgConfig.branding?.custom_css ?? null,
            showPoweredBy: orgConfig.branding?.show_powered_by ?? true,
          },
          registration: {
            mode: orgConfig.registration?.mode ?? 'open',
            allowedDomains: orgConfig.registration?.allowed_domains ?? [],
          },
          oauth: {
            managed: orgConfig.oauth?.managed ?? false,
            providers: orgConfig.oauth?.providers ?? ['google', 'github'],
          },
          loading: false,
          error: null,
        })

        // Apply branding
        applyOrgBranding(orgConfig)
      } catch (err) {
        console.error('Failed to load org config:', err)
        setState({
          ...defaultContext,
          slug,
          loading: false,
          error: 'Failed to load organization configuration',
        })
      }
    }

    fetchOrg()
  }, [])

  return (
    <OrgContext.Provider value={state}>
      {children}
    </OrgContext.Provider>
  )
}

/**
 * Apply org branding to the page
 */
function applyOrgBranding(orgConfig: {
  name: string
  branding?: {
    logo_url?: string | null
    favicon_url?: string | null
    primary_color?: string | null
    secondary_color?: string | null
    custom_css?: string | null
    show_powered_by?: boolean
  }
}) {
  // Apply favicon
  if (orgConfig.branding?.favicon_url) {
    const link = document.querySelector("link[rel~='icon']") as HTMLLinkElement
    if (link) {
      link.href = orgConfig.branding.favicon_url
    } else {
      const newLink = document.createElement('link')
      newLink.rel = 'icon'
      newLink.href = orgConfig.branding.favicon_url
      document.head.appendChild(newLink)
    }
  }

  // Apply CSS variables for colors
  const root = document.documentElement

  if (orgConfig.branding?.primary_color) {
    root.style.setProperty('--org-primary-color', orgConfig.branding.primary_color)
    // Also generate shades for Tailwind-like usage
    root.style.setProperty('--org-primary-50', adjustBrightness(orgConfig.branding.primary_color, 0.95))
    root.style.setProperty('--org-primary-100', adjustBrightness(orgConfig.branding.primary_color, 0.9))
    root.style.setProperty('--org-primary-200', adjustBrightness(orgConfig.branding.primary_color, 0.8))
    root.style.setProperty('--org-primary-300', adjustBrightness(orgConfig.branding.primary_color, 0.6))
    root.style.setProperty('--org-primary-400', adjustBrightness(orgConfig.branding.primary_color, 0.4))
    root.style.setProperty('--org-primary-500', orgConfig.branding.primary_color)
    root.style.setProperty('--org-primary-600', adjustBrightness(orgConfig.branding.primary_color, -0.1))
    root.style.setProperty('--org-primary-700', adjustBrightness(orgConfig.branding.primary_color, -0.2))
    root.style.setProperty('--org-primary-800', adjustBrightness(orgConfig.branding.primary_color, -0.3))
    root.style.setProperty('--org-primary-900', adjustBrightness(orgConfig.branding.primary_color, -0.4))
  }

  if (orgConfig.branding?.secondary_color) {
    root.style.setProperty('--org-secondary-color', orgConfig.branding.secondary_color)
  }

  // Apply custom CSS
  if (orgConfig.branding?.custom_css) {
    const styleId = 'org-custom-css'
    let styleEl = document.getElementById(styleId) as HTMLStyleElement
    if (!styleEl) {
      styleEl = document.createElement('style')
      styleEl.id = styleId
      document.head.appendChild(styleEl)
    }
    styleEl.textContent = orgConfig.branding.custom_css
  }

  // Update page title
  document.title = `${orgConfig.name} - Sign in`
}

/**
 * Adjust color brightness
 * @param hex - Hex color string (e.g., '#4f46e5')
 * @param percent - Brightness adjustment (-1 to 1, negative = darker)
 */
function adjustBrightness(hex: string, percent: number): string {
  // Remove # if present
  hex = hex.replace(/^#/, '')

  // Parse RGB
  const r = parseInt(hex.substring(0, 2), 16)
  const g = parseInt(hex.substring(2, 4), 16)
  const b = parseInt(hex.substring(4, 6), 16)

  // Adjust brightness
  const adjust = (value: number) => {
    if (percent > 0) {
      // Lighten: blend with white
      return Math.round(value + (255 - value) * percent)
    } else {
      // Darken: blend with black
      return Math.round(value * (1 + percent))
    }
  }

  const newR = Math.min(255, Math.max(0, adjust(r)))
  const newG = Math.min(255, Math.max(0, adjust(g)))
  const newB = Math.min(255, Math.max(0, adjust(b)))

  // Convert back to hex
  return `#${newR.toString(16).padStart(2, '0')}${newG.toString(16).padStart(2, '0')}${newB.toString(16).padStart(2, '0')}`
}

/**
 * Hook to access org context
 */
export function useOrg(): OrgContextValue {
  return useContext(OrgContext)
}
